import {
  replaceEqualDeep,
  useMutation,
  useQueryClient,
  type MutationState,
  type QueryClient,
} from '@tanstack/react-query'
import { useCallback, useSyncExternalStore } from 'react'
import {
  attachTriageEntryMutation,
  closeTriageEntryMutation,
  dismissAiDraftMutation,
  isProblem,
  listConversationsQueryKey,
  listTriageQueryKey,
  sendAiDraftMutation,
  sendMessageMutation,
  setConversationModeMutation,
  type MessageChannel,
  type Options,
  type SendMessageData,
  type SetConversationModeData,
  type Timeline,
  type TimelineMessage,
} from '@/shared/api'
import { conversationQuery, timelineQuery } from './queries'

// Тип выводим из контракта: именованной схемы у `mode` нет, кодоген повторил инлайновый enum
// литералом в двух местах, и рукописная копия молча не заметила бы третий режим.
export type ConversationMode = SetConversationModeData['body']['mode']

/**
 * Дописать в ленту сообщение, вернувшееся с сервера, дедуплицируя по `id`.
 *
 * ⚠️ ДЕДУП НЕОБХОДИМ: лента опрашивается раз в пять секунд (`POLLING.conversation`), и тик может
 * приземлиться между уходом `POST` и приходом `201` — тогда сервер уже отдал это сообщение, и пузырь
 * задвоился бы.
 *
 * ⚠️ НА КОЛЛИЗИИ ПОБЕЖДАЕТ КОПИЯ ИЗ КЕША — она свежее: в ответе на `sendMessage` контракт отдаёт
 * статус доставки `sending`, а копия из поллинга могла уже стать `sent`/`read`. Перезапись откатила
 * бы галочку доставки.
 *
 * Разделитель дня не добавляем: их шлёт сервер (лента приходит сгруппированной), и клиентская копия
 * этой логики разъехалась бы с серверной.
 */
export function appendMessage(timeline: Timeline, message: TimelineMessage): Timeline {
  if (timeline.items.some((item) => item.id === message.id)) return timeline
  return { ...timeline, items: [...timeline.items, message] }
}

/**
 * Приземлить отправленное сообщение в ленту так, чтобы поллинг его не съел.
 *
 * ⚠️ ОДНОЙ ЗАПИСИ В КЕШ НЕДОСТАТОЧНО, И ЭТО ЗАМЕРЕНО. GET поллинга, бывший в полёте в момент прихода
 * `201`, несёт снимок ленты ДО появления сообщения на сервере и, приземляясь, заменяет ленту целиком:
 * зонд показал последовательность `setQueryData → [день, м-1, м-2]`, затем ответ поллинга →
 * `[день, м-1]`, то есть пузырь исчезает с экрана до следующего тика. Последствие не
 * косметическое: менеджер отправит сообщение заново ЖИВОМУ гостю.
 *
 * ⚠️ КАНОНИЧЕСКИЙ РЕЦЕПТ (`cancelQueries` в `onMutate`) ЗДЕСЬ НЕ РАБОТАЕТ — проверено: тик,
 * стартовавший во время `POST`, всё равно затирает. Отменять надо в `onSuccess`, ПЕРЕД записью:
 * летящий рефетч к этому моменту заведомо устарел, и его ответ надо отбросить, а не дождаться.
 */
export async function settleSentMessage(
  queryClient: QueryClient,
  conversationId: string,
  message: TimelineMessage,
): Promise<void> {
  const queryKey = timelineQuery(conversationId).queryKey

  await queryClient.cancelQueries({ queryKey })

  // Ленты может ещё не быть в кеше. Возврат `undefined` из апдейтера — это ОТКАЗ от записи, а не
  // запись `undefined` (проверено зондом): иначе лента получила бы `data: undefined` вместо скелетона.
  queryClient.setQueryData(queryKey, (timeline) =>
    timeline ? appendMessage(timeline, message) : undefined,
  )
}

/** Одно логическое сообщение: текст плюс ключ, который переживает повторы. */
export type OutgoingMessage = {
  text: string
  /**
   * ⚠️ Мнётся ОДИН раз на сообщение и переиспользуется при КАЖДОМ повторе: новый ключ на «Повторить»
   * означает второе сообщение живому гостю. Правило целиком — в `shared/api/idempotency.ts`.
   */
  idempotencyKey: string
  /**
   * Каким родом канала слать. Без него сервер шлёт каналом по умолчанию. Часть СООБЩЕНИЯ, а не
   * вызова: повтор обязан уйти тем же каналом, что и первая попытка.
   */
  channelKind?: MessageChannel['kind']
}

/*
 * ⚠️ НИЖЕ ОБРАБОТЧИКИ БЕРУТ ПЕРЕПИСКУ ИЗ `variables.path`, А НЕ ИЗ ПАРАМЕТРА ХУКА. Сменись переписка
 * без размонтирования, `useMutation` отдал бы свежие опции ЕЩЁ ЛЕТЯЩЕЙ мутации
 * (`MutationObserver.setOptions`, ветка `pending`) — и из замыкания ответ на отправку в А лёг бы в
 * ленту Б. Заслон — `mutations.test.tsx`.
 */

/** По этому ключу очередь неотправленного находит попытки отправки в кеше мутаций. */
const SEND_MESSAGE_KEY = ['conversations', 'sendMessage']

type SendVariables = Options<SendMessageData>

const sendVariables = (
  conversationId: string,
  { text, idempotencyKey, channelKind }: OutgoingMessage,
): SendVariables => ({
  path: { conversationId },
  body: { text, channelKind },
  headers: { 'Idempotency-Key': idempotencyKey },
})

type SendState = MutationState<TimelineMessage, unknown, SendVariables, unknown>

const sendAttempts = (queryClient: QueryClient) =>
  queryClient
    .getMutationCache()
    .findAll({ mutationKey: SEND_MESSAGE_KEY })
    .map((attempt) => ({ attempt, state: attempt.state as SendState }))

const attemptsOf = (queryClient: QueryClient, idempotencyKey: string) =>
  sendAttempts(queryClient).filter(
    ({ state }) => state.variables?.headers['Idempotency-Key'] === idempotencyKey,
  )

/** Стереть все попытки сообщения: его ушедшую копию держит лента — или менеджер его убрал. */
export function forgetUnsent(queryClient: QueryClient, idempotencyKey: string): void {
  for (const { attempt } of attemptsOf(queryClient, idempotencyKey)) {
    queryClient.getMutationCache().remove(attempt)
  }
}

/** Ждёт ли сообщение в очереди: выход из аккаунта сносит попытки вместе с кешем. */
export function isUnsent(queryClient: QueryClient, idempotencyKey: string): boolean {
  return attemptsOf(queryClient, idempotencyKey).length > 0
}

/** Летит ли попытка сообщения: второй повтор не уходит, пока не кончилась первая. */
export function isSending(queryClient: QueryClient, idempotencyKey: string): boolean {
  return attemptsOf(queryClient, idempotencyKey).some(({ state }) => state.status === 'pending')
}

/**
 * Отправка сообщения гостю.
 *
 * ⚠️ ОПТИМИСТИЧНОЙ ЗАПИСИ В ЛЕНТУ НЕТ ОСОЗНАННО: каждый успешный рефетч заменяет ленту ЦЕЛИКОМ, и
 * записи, которой нет на сервере, хватило бы до следующего тика. Неотправленное — ПОПЫТКИ ОТПРАВКИ
 * в кеше мутаций (`useUnsentMessages`): они переживают уход с экрана, `gcTime: Infinity` держит
 * упавшие, пока сообщение не уйдёт или менеджер его не уберёт, — успех стирает все попытки ключа.
 *
 * На успехе дописываем то, что вернул СЕРВЕР, через `settleSentMessage` (он сперва гасит летящий
 * поллинг), и инвалидируем шапку и список диалогов — у строки меняются превью и время.
 *
 * ⚠️ Отказ сообщает вызывающий (`reportedByCaller`) в `catch` у `mutateAsync`: общий тост не знает,
 * видна ли очередь этой переписки, — наблюдатель мутации держит лишь последнюю попытку хука.
 */
export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...sendMessageMutation(),
    mutationKey: SEND_MESSAGE_KEY,
    gcTime: Infinity,
    meta: { reportedByCaller: true },
    onSuccess: async (message, { path, headers }) => {
      await settleSentMessage(queryClient, path.conversationId, message)
      forgetUnsent(queryClient, headers['Idempotency-Key'])
      // Ответ менеджера закрывает черновик ИИ и переводит переписку в `manager` — шапка устарела.
      void queryClient.invalidateQueries({ queryKey: conversationQuery(path.conversationId).queryKey })
      void queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() })
    },
  })

  return {
    mutateAsync: (message: OutgoingMessage) =>
      mutation.mutateAsync(sendVariables(conversationId, message)),
  }
}

/** Сообщение без «отправлено»: летит (без сети — ждёт на паузе) или упало. */
export type UnsentMessage = OutgoingMessage & {
  /** Когда нажали «Отправить» — первая попытка, а не последний повтор. */
  sentAt: number
  status: 'sending' | 'failed'
  /** Отказ последней попытки; у `sending` — `null`. */
  error: unknown
  /** Отказы всех упавших попыток по порядку: по ним видно, могла ли какая-то дойти. */
  failures: unknown[]
}

/** Прошлый снимок по переписке: `useSyncExternalStore` требует тот же объект, пока ничего не менялось. */
const snapshots = new WeakMap<QueryClient, Map<string, UnsentMessage[]>>()

function readUnsent(queryClient: QueryClient, conversationId: string): UnsentMessage[] {
  const unsent = new Map<string, UnsentMessage>()
  for (const { state } of sendAttempts(queryClient)) {
    // У `idle` ещё нет переменных. Успешных не бывает — успех стирает ВСЕ попытки своего ключа, и
    // упавшая до повтора уходит вместе с ним.
    const variables = state.variables
    if (!variables || variables.path.conversationId !== conversationId) continue
    const idempotencyKey = variables.headers['Idempotency-Key']
    const first = unsent.get(idempotencyKey)
    const failures = first?.failures ?? []
    unsent.set(idempotencyKey, {
      text: variables.body.text,
      idempotencyKey,
      channelKind: variables.body.channelKind,
      sentAt: first?.sentAt ?? state.submittedAt,
      status: state.status === 'error' ? 'failed' : 'sending',
      error: state.error,
      failures: state.status === 'error' ? [...failures, state.error] : failures,
    })
  }
  const byConversation = snapshots.get(queryClient) ?? new Map<string, UnsentMessage[]>()
  snapshots.set(queryClient, byConversation)
  const shared = replaceEqualDeep(byConversation.get(conversationId), [...unsent.values()])
  byConversation.set(conversationId, shared)
  return shared
}

/**
 * Неотправленное этой переписки — из попыток отправки в кеше мутаций, по одному на ключ
 * идемпотентности: статус и отказ — последней попытки, время — первой.
 *
 * ⚠️ Своё чтение кеша, а не `useMutationState`: тот читает кеш только в слушателе, и исход,
 * пришедший между рендером и подпиской, остался бы невидим до следующего события кеша — пузырь
 * «отправляется» без «Повторить». Здесь снимок читается на каждом рендере и сразу после подписки.
 */
export function useUnsentMessages(conversationId: string): UnsentMessage[] {
  const queryClient = useQueryClient()
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getMutationCache().subscribe((event) => {
        // `clear()` рассылает `removed`, ещё не очистив кеш: прочитанное сразу было бы прежним.
        if (event.type === 'removed') queueMicrotask(onChange)
        else onChange()
      }),
    [queryClient],
  )
  return useSyncExternalStore(subscribe, () => readUnsent(queryClient, conversationId))
}

/**
 * Отправить черновик ИИ как есть. `draftId` — черновик, который видел менеджер: сервер отправит
 * только его, а не тот, что висит у него в момент клика. Править черновик нельзя — переписку
 * переводят в режим `manager` и шлют `sendMessage`.
 *
 * ⚠️ `409` — НЕ ОШИБКА, А УСТАРЕВШИЙ ЭКРАН: черновика с этим id среди ожидающих нет, и ушёл ли он
 * гостю, отказ не говорит, поэтому утверждать «отправлено» нельзя. На любой ответ-отказ тред
 * перезапрашиваем: на экране не то, что на сервере (`409`), в ленте мог появиться неотправленный
 * пузырь (`422`), исход неизвестен (5xx). На обрыве сети — нет: сервер не ответил, а шапку и ленту
 * подтянет их опрос.
 *
 * Общий тост молчит, пока на мутацию смотрит карточка (`meta.handledInPlace`): отказ она говорит сама,
 * а тост назвал бы `409` поломкой приложения. Отпустила карточка мутацию — тост назовёт `subject`.
 */
export function useSendAiDraft(conversationId: string, subject: string) {
  const queryClient = useQueryClient()

  // ⚠️ Шапка и лента лежат под РАЗНЫМИ ключами — один вызов их не накрывает (проверено зондом).
  // Гасить летящий поллинг, как в `settleSentMessage`, здесь НЕ нужно: `invalidateQueries` сам
  // отменяет активный рефетч. Разница в том, что там мы кладём данные руками и рефетч их затирает.
  const refreshThread = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: conversationQuery(id).queryKey })
    void queryClient.invalidateQueries({ queryKey: timelineQuery(id).queryKey })
    void queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() })
  }

  const mutation = useMutation({
    ...sendAiDraftMutation(),
    meta: { handledInPlace: true, subject },
    onSuccess: (_message, { path }) => refreshThread(path.conversationId),
    onError: (error, { path }) => {
      if (isProblem(error)) refreshThread(path.conversationId)
    },
  })

  const vars = (draftId: string) => ({ path: { conversationId }, body: { draftId } })

  return {
    ...mutation,
    mutate: (draftId: string) => mutation.mutate(vars(draftId)),
    mutateAsync: (draftId: string) => mutation.mutateAsync(vars(draftId)),
  }
}

/**
 * Отклонить черновик ИИ, который видел менеджер (`draftId`). `204` — и тогда, когда черновика с этим
 * id среди ожидающих уже нет: отклонять нечего, и это не ошибка. Более новый, которого менеджер не
 * видел, вызов не трогает — его покажет перезапрос шапки.
 *
 * Инвалидируем только шапку: в строке списка черновика нет, гостю ничего не отправлено — превью,
 * время и счётчик «ждёт ответа» не изменились.
 */
export function useDismissAiDraft(conversationId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...dismissAiDraftMutation(),
    onSuccess: (_data, { path }) => {
      void queryClient.invalidateQueries({ queryKey: conversationQuery(path.conversationId).queryKey })
    },
  })

  const vars = (draftId: string) => ({ path: { conversationId }, body: { draftId } })

  return {
    ...mutation,
    mutate: (draftId: string) => mutation.mutate(vars(draftId)),
    mutateAsync: (draftId: string) => mutation.mutateAsync(vars(draftId)),
  }
}

/**
 * Переключить режим переписки (менеджер ↔ ИИ).
 *
 * Ответ — свежая `ConversationDetail`, кладём её в кеш напрямую, чтобы тред переключился в тот же
 * кадр. Гасить черновик отдельно не нужно: переключение на `manager` отклоняет висящий `aiDraft` на
 * сервере, и пришедший ответ это отражает.
 *
 * ⚠️ СПИСОК ИНВАЛИДИРУЕМ ПО ДВУМ ПРИЧИНАМ, и вторая важнее: в строке меняется подпись «Отвечает ИИ»
 * (`ConversationListItem`), но главное — меняется её ЧЛЕНСТВО в выборке, потому что фильтр `ai`
 * отбирает переписки по тому самому полю, которое эта мутация переключает. Без инвалидации
 * переписка, снятая с ИИ, осталась бы висеть в фильтре «ИИ».
 */
export function useSetConversationMode(conversationId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...setConversationModeMutation(),
    onSuccess: (detail, { path }) => {
      queryClient.setQueryData(conversationQuery(path.conversationId).queryKey, detail)
      void queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() })
    },
  })

  return {
    ...mutation,
    mutate: (mode: ConversationMode) =>
      mutation.mutate({ path: { conversationId }, body: { mode } }),
    mutateAsync: (mode: ConversationMode) =>
      mutation.mutateAsync({ path: { conversationId }, body: { mode } }),
  }
}

/**
 * `404` у операций разбора значит «записи уже нет» — её разобрал другой менеджер. Перечитываем
 * очередь и на отказе: иначе строка, которую не удалось закрыть, осталась бы на экране, а текст
 * ошибки звучал бы рассказом про что-то постороннее.
 */
function refreshTriageOnGone(queryClient: QueryClient, error: unknown): void {
  if (isProblem(error) && error.status === 404) {
    void queryClient.invalidateQueries({ queryKey: listTriageQueryKey() })
  }
}

/*
 * Обе операции разбора инвалидируют очередь ЦЕЛИКОМ, а не вычёркивают строку из кеша: страница
 * разбора бывает пустой при живом курсоре, и «убрать одну» оставило бы экран с пустым списком и
 * живым курсором — в состоянии, которое он обязан дочитывать, но уже дочитал.
 *
 * ⚠️ Список переписок инвалидируем ТОЛЬКО после привязки: там появился новый диалог. Закрытие записи
 * переписки не создаёт вовсе.
 */
export function useAttachTriageEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    ...attachTriageEntryMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listTriageQueryKey() })
      void queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() })
    },
    onError: (error) => refreshTriageOnGone(queryClient, error),
  })
}

export function useCloseTriageEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    ...closeTriageEntryMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listTriageQueryKey() })
    },
    onError: (error) => refreshTriageOnGone(queryClient, error),
  })
}
